import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Input } from '~/components/ui';
import { useDeclineQuote } from '~/api';
import { toastGraphQLError } from '~/utils';
import type { NegotiableQuote } from './quoteNegotiation';

export function DeclineQuoteAction({
  quote,
}: Readonly<{ quote: NegotiableQuote }>) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <DeclineQuoteButton
        quoteId={quote.id}
        isOpen={isOpen}
        onToggle={() => setIsOpen((current) => !current)}
      />
      {isOpen && (
        <DeclineQuoteForm quote={quote} onDeclined={() => setIsOpen(false)} />
      )}
    </div>
  );
}

function DeclineQuoteButton({
  quoteId,
  isOpen,
  onToggle,
}: Readonly<{ quoteId: string; isOpen: boolean; onToggle: () => void }>) {
  const { t } = useTranslation('rfq-view');

  return (
    <Button
      variant="destructive-outline"
      size="sm"
      width="auto"
      data-testid={`decline-quote-${quoteId}`}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      {t('rfq_view.decline_quote')}
    </Button>
  );
}

function DeclineQuoteForm({
  quote,
  onDeclined,
}: Readonly<{ quote: NegotiableQuote; onDeclined: () => void }>) {
  const { t } = useTranslation('rfq-view');
  const { mutateAsync: declineQuote, isPending: isDeclining } =
    useDeclineQuote();
  const [reason, setReason] = useState('');

  const handleDecline = async () => {
    try {
      await declineQuote({ quoteId: quote.id, reason: reason.trim() || null });
      toast.success(t('rfq_view.quote_declined'));
      onDeclined();
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        name={`${quote.id}-decline-reason`}
        aria-label={t('rfq_view.decline_reason_label')}
        placeholder={t('rfq_view.decline_reason_label')}
        containerClassName="min-w-[160px]"
        value={reason}
        onChange={(_, value) => setReason(value)}
      />
      <Button
        variant="destructive"
        size="sm"
        width="auto"
        data-testid={`confirm-decline-${quote.id}`}
        onClick={() => void handleDecline()}
        disabled={isDeclining}
        loading={isDeclining}
      >
        {t('rfq_view.decline_quote')}
      </Button>
    </div>
  );
}
